import Vue from 'vue';
import {Route} from 'vue-router';
import store from '@/store';
import {PaletteEntity} from '@/components/app/search/searchEntities';
import {canReadArea} from '@/components/app/search/searchAccess';

/** Areas that can be visited: palette areas (incl. trainings) plus schools of acquisition lists */
export type VisitEntity = PaletteEntity | 'acquisitionListItems';

export type RecentVisit = {
    path: string;
    title: string;
    entity: VisitEntity;
    id: string;
    time: number;
};

export type PinnedEntry = {
    entity: PaletteEntity;
    id: string;
    title: string;
    subtitle?: string;
    time: number;
};

type SearchMemory = {
    visits: RecentVisit[];
    pins: PinnedEntry[];
    searches: string[];
};

const MAX_VISITS = 10;
const MAX_PINS = 10;
const MAX_SEARCHES = 8;
const STORAGE_PREFIX = 'vanilla.searchPalette.v1.';

export const VISIT_LABELS: Record<VisitEntity, string> = {
    students: 'Schüler:in',
    parents: 'Elternteil',
    employees: 'Mitarbeiter:in',
    teachers: 'Lehrkraft',
    applicants: 'Bewerber:in',
    institutions: 'Standort',
    courses: 'Kurs',
    products: 'Produkt',
    trainings: 'Schulung',
    acquisitionListItems: 'Akquise-Standort',
};

/** Shared reactive state, so pins and recent entries update in every open component */
const state = Vue.observable({
    userId: '' as string,
    visits: [] as RecentVisit[],
    pins: [] as PinnedEntry[],
    searches: [] as string[],
});

function currentUserId(): string {
    const userId = store.state.loginUserId || (store.state.user as { _id?: unknown } | null)?._id;
    return userId ? String(userId) : '';
}

function persist() {
    if (!state.userId) {
        return;
    }
    try {
        const memory: SearchMemory = {visits: state.visits, pins: state.pins, searches: state.searches};
        localStorage.setItem(STORAGE_PREFIX + state.userId, JSON.stringify(memory));
    } catch (e) {
        // Storage is optional (private mode, quota)
    }
}

/** Loads the stored entries of the logged in user (again if the user changed) */
function ensureLoaded(): boolean {
    const userId = currentUserId();
    if (!userId) {
        return false;
    }
    if (state.userId === userId) {
        return true;
    }
    state.userId = userId;
    state.visits = [];
    state.pins = [];
    state.searches = [];
    try {
        const raw = localStorage.getItem(STORAGE_PREFIX + userId);
        const memory = raw ? JSON.parse(raw) as Partial<SearchMemory> : null;
        if (memory) {
            state.visits = Array.isArray(memory.visits) ? memory.visits.slice(0, MAX_VISITS) : [];
            state.pins = Array.isArray(memory.pins) ? memory.pins.slice(0, MAX_PINS) : [];
            state.searches = Array.isArray(memory.searches) ? memory.searches.slice(0, MAX_SEARCHES) : [];
        }
    } catch (e) {
        // Broken or blocked storage: start empty
    }
    return true;
}

export const SearchMemoryStore = {
    /** Loads the entries of the logged in user; call before rendering them */
    load(): void {
        ensureLoaded();
    },
    get visits(): RecentVisit[] {
        ensureLoaded();
        return state.visits;
    },
    get pins(): PinnedEntry[] {
        ensureLoaded();
        return state.pins;
    },
    get searches(): string[] {
        ensureLoaded();
        return state.searches;
    },
    isPinned(entity: PaletteEntity, id: string): boolean {
        return this.pins.some((el) => el.entity === entity && el.id === id);
    },
    togglePin(entry: Omit<PinnedEntry, 'time'>) {
        if (!ensureLoaded()) {
            return;
        }
        if (this.isPinned(entry.entity, entry.id)) {
            state.pins = state.pins.filter((el) => !(el.entity === entry.entity && el.id === entry.id));
        } else {
            state.pins = [{...entry, time: Date.now()}, ...state.pins].slice(0, MAX_PINS);
        }
        persist();
    },
    addSearch(term: string) {
        const value = term.trim();
        if (!value || value.length < 2 || !ensureLoaded()) {
            return;
        }
        state.searches = [value, ...state.searches.filter((el) => el.toLowerCase() !== value.toLowerCase())].slice(0, MAX_SEARCHES);
        persist();
    },
    clearSearches() {
        if (!ensureLoaded()) {
            return;
        }
        state.searches = [];
        persist();
    },
    addVisit(visit: Omit<RecentVisit, 'time'>) {
        if (!canReadArea(visit.entity) || !ensureLoaded()) {
            return;
        }
        const others = state.visits.filter((el) => !(el.entity === visit.entity && el.id === visit.id));
        state.visits = [{...visit, time: Date.now()}, ...others].slice(0, MAX_VISITS);
        persist();
    },
};

const STATUS_KEY = 'status.';
const STATUSES = ['active', 'inactive', 'all'];

/** Last chosen status filter (Aktiv / Inaktiv / Alle) of the logged in user, null if none is stored */
export function loadSearchStatus<T extends string>(): T | null {
    const userId = currentUserId();
    if (!userId) {
        return null;
    }
    try {
        const value = localStorage.getItem(STORAGE_PREFIX + STATUS_KEY + userId);
        return value && STATUSES.includes(value) ? value as T : null;
    } catch (e) {
        return null;
    }
}

export function saveSearchStatus(status: string): void {
    const userId = currentUserId();
    if (!userId) {
        return;
    }
    try {
        localStorage.setItem(STORAGE_PREFIX + STATUS_KEY + userId, status);
    } catch (e) {
        // Storage is optional
    }
}

/** Logout: forget the entries in memory and in the storage of this browser */
function clearMemory() {
    if (state.userId) {
        try {
            localStorage.removeItem(STORAGE_PREFIX + state.userId);
            localStorage.removeItem(STORAGE_PREFIX + STATUS_KEY + state.userId);
        } catch (e) {
            // Storage is optional
        }
    }
    state.userId = '';
    state.visits = [];
    state.pins = [];
    state.searches = [];
}

store.subscribe((mutation) => {
    if (mutation.type === 'logout') {
        clearMemory();
    }
});

/** Route patterns of profile / detail views that are remembered as "Zuletzt besucht" */
const VISIT_ROUTES: Array<{ path: string; entity: VisitEntity; param: string }> = [
    {path: '/students/:studentId', entity: 'students', param: 'studentId'},
    {path: '/parents/:parentId', entity: 'parents', param: 'parentId'},
    {path: '/employees/:employeeId', entity: 'employees', param: 'employeeId'},
    {path: '/teachers/:employeeId', entity: 'teachers', param: 'employeeId'},
    {path: '/applicants/:employeeId', entity: 'applicants', param: 'employeeId'},
    {path: '/recruiter/applicants/:employeeId', entity: 'applicants', param: 'employeeId'},
    {path: '/institutions/:institutionId', entity: 'institutions', param: 'institutionId'},
    {path: '/courses/:courseId', entity: 'courses', param: 'courseId'},
    {path: '/trainings/:trainingId', entity: 'trainings', param: 'trainingId'},
    {path: '/acquisitionLists/:acquisitionListId/:institutionId', entity: 'acquisitionListItems', param: 'institutionId'},
];

const DEFAULT_TITLE = 'Iceesoft Vanilla';
const TITLE_POLL_MS = 300;
const TITLE_TIMEOUT_MS = 8_000;

let visitTimer: number | undefined;

/**
 * Remembers visits of profile / detail views. The views set document.title after loading their data,
 * so the title is taken as soon as it differs from the route name (which the router sets first).
 */
export function trackRecentVisit(to: Route) {
    if (visitTimer) {
        clearInterval(visitTimer);
        visitTimer = undefined;
    }
    const matched = to.matched[to.matched.length - 1];
    const config = matched ? VISIT_ROUTES.find((el) => el.path === matched.path) : undefined;
    const id = config ? to.params[config.param] : undefined;
    if (!config || !id) {
        return;
    }
    const startedAt = Date.now();
    const routeName = to.name || '';
    visitTimer = setInterval(() => {
        const title = document.title;
        const titleReady = !!title && title !== routeName && title !== DEFAULT_TITLE;
        if (titleReady) {
            SearchMemoryStore.addVisit({path: to.fullPath, title, entity: config.entity, id});
        }
        if (titleReady || Date.now() - startedAt > TITLE_TIMEOUT_MS) {
            clearInterval(visitTimer);
            visitTimer = undefined;
        }
    }, TITLE_POLL_MS) as unknown as number;
}
